import React, { useState, useEffect, useRef } from 'react';
import { Accordion, BhiDialog, Button, Dropdown, Panel, TextInput } from 'src/UIKit';
import { DashedDropZone } from './components/DashedDropZone';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactRouterPrompt from 'react-router-prompt';
import {
  Typography,
  Box,
  Stack,
  AccordionSummary,
  AccordionDetails,
  DialogContent,
  DialogActions,
  CircularProgress,
  Backdrop,
  Switch,
} from '@mui/material';
import {
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { Breadcrumb } from 'src/components/Breadcrumb';
import {
  QRCodeDestinationType,
  QRCodeFileFormat,
  QRCodeSize,
  QRCodeStylingOptions,
  QRCodeType,
  EntityQRCodeParametersSimpleView,
  QRCodeSaveParameters,
} from '../../types';
import { QRCodeAppearanceOptions } from './components/QRCodeAppearanceOptions';
import { ScanDestinationSection } from './components/ScanDestinationSection';
import { useGetKeywordQRCodeApi, useGetQRCodeApi } from '../../api';
import { reportErrorToUser } from 'src/api/axiosClient/errorHandling';
import { useSelector } from 'react-redux';
import { getCompanyById, getCurrentCompanyId, getCurrentCompanyName } from 'src/modules/companies/slices';
import { DEFAULT_QR_SAVE_PARAMETERS,
  getDestinationDataFromQrData, getDestinationTypeFromQrData, getQRCodeSaveParameters,
  handleDownload as handleDownloadImpl, renderQrCodePreview, validateAllFields } from '../../helpers/helpers';
import { QRCodeStoredDestination, qrCodeWriteData } from '../../helpers/buildQRCodeWriteBody';
import { useQRCodeSave } from '../../hooks/useQRCodeSave';
import QRCodeStyling from 'qr-code-styling';

interface QRCodeFormData extends QRCodeSaveParameters {
  id: string | null;
  keywordId: string | null;
  name: string;
  destinationType: QRCodeDestinationType;
}

const formatOptions: { value: QRCodeFileFormat; title: string }[] = [
  { value: 'PNG', title: 'PNG' },
  { value: 'JPG', title: 'JPG' },
  { value: 'SVG', title: 'SVG' },
];

const sizeOptions: { value: QRCodeSize; title: string }[] = [
  { value: 'Small', title: 'Small (200 x 200)' },
  { value: 'Medium', title: 'Medium (600 x 600)' },
  { value: 'Large', title: 'Large (1200 x 1200)' },
];

export default function CreateEditQRCode() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // A stored code is edited by its id. A code generated from a keyword has no record yet, so it is
  // opened by that keyword, and saving it creates one.
  const editCompanyId = searchParams.get('companyId');
  const editId = searchParams.get('id');
  const editKeywordId = searchParams.get('keywordId');
  const isEditMode = Boolean(editCompanyId && (editId || editKeywordId));
  // A bookmark of the older links carries a type and a text and no id: left alone it opens as a
  // creation form with an empty destination.
  const isStaleEditLink = !isEditMode && (searchParams.has('type') || searchParams.has('text'));

  const currentCompanyId = useSelector(getCurrentCompanyId);
  const getQRCodeApi = useGetQRCodeApi();
  const getKeywordQRCodeApi = useGetKeywordQRCodeApi();
  const { isSaving, save } = useQRCodeSave();

  // The download names the company the code belongs to, not the one picked in the header: the two
  // part ways as soon as the company is switched while this page is open.
  const currentCompanyName = useSelector(getCurrentCompanyName);
  const editedCompany = useSelector(getCompanyById(editCompanyId ?? ''));
  const companyName = editedCompany?.name || currentCompanyName;
  
  // Parse QR code data and populate form
  const parseQRCodeData = (qrCode: EntityQRCodeParametersSimpleView) => {
    const destinationType = getDestinationTypeFromQrData(qrCode.qrcodeTypeName, qrCode.qrCodeData);
    const destinationData = getDestinationDataFromQrData(qrCode.qrCodeData, destinationType);

    const parameters: QRCodeSaveParameters = getQRCodeSaveParameters(qrCode.parameters);

    return {
      name: qrCode.name,
      destinationType,
      destinationData,
      previewFormat: parameters.previewFormat,
      previewSize: parameters.previewSize,
      styling: parameters.styling,
    };
  };

  const [formData, setFormData] = useState<QRCodeFormData>({
    id: null,
    keywordId: null,
    name: '',
    destinationType: 'URL',
    destinationData: {},
    previewFormat: DEFAULT_QR_SAVE_PARAMETERS.previewFormat,
    previewSize: DEFAULT_QR_SAVE_PARAMETERS.previewSize,
    styling: DEFAULT_QR_SAVE_PARAMETERS.styling,
  });

  const [storedDestination, setStoredDestination] = useState<QRCodeStoredDestination | null>(null);

  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isEditedCodeLoaded, setIsEditedCodeLoaded] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState<'destination' | 'design' | null>(
    isEditMode ? 'design' : 'destination',
  );
  const [initialFormSnapshot, setInitialFormSnapshot] = useState<string>('');
  const [lastCustomColor, setLastCustomColor] = useState<string>('#FFFFFF');
  const [isQrCodeReady, setIsQrCodeReady] = useState(false);

  const writeData = (): string => qrCodeWriteData(formData, formData.destinationType, storedDestination);

  // Local state for text fields to prevent constant QR code updates
  const [localTopText, setLocalTopText] = useState('');
  const [localBottomText, setLocalBottomText] = useState('');

  // Trigger for QR code refresh - only updates on blur events
  const [qrRefreshTrigger, setQrRefreshTrigger] = useState(0);
  const [cachedQrData, setCachedQrData] = useState<string>('');

  const qrContainerRef = useRef<HTMLDivElement | null>(null);
  const logoFileInputRef = useRef<HTMLInputElement | null>(null);

  const qrInstanceRef = useRef<QRCodeStyling | null>(null);

  // Sync local text state with form data
  useEffect(() => {
    setLocalTopText(formData.styling.topText || '');
    setLocalBottomText(formData.styling.bottomText || '');
  }, [formData.styling.topText, formData.styling.bottomText]);

  // Initialize cached QR data on destination type change only
  useEffect(() => {
    setCachedQrData(writeData());
  }, [formData.destinationType]);

  useEffect(() => {
    if (formData.destinationType === 'Contact Card') {
      setCachedQrData(writeData());
      setQrRefreshTrigger(prev => prev + 1);
    }
  }, [formData.destinationData.vcardData]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      if (!qrContainerRef.current) return;

      if (!isMounted) return;

      const previewBoxSize = 280;

      const instance: QRCodeStyling = new QRCodeStyling({
        width: previewBoxSize,
        height: previewBoxSize,
        type: 'svg',
        data: 'urlto:https://example.com',
        margin: 20,
        qrOptions: {},
        dotsOptions: { color: '#000000', type: 'square' },
        backgroundOptions: { color: '#FFFFFF' },
      });

      qrInstanceRef.current = instance;
      qrContainerRef.current.innerHTML = '';
      instance.append(qrContainerRef.current);
    })();
    return () => {
      isMounted = false;
      if (qrContainerRef.current) {
        qrContainerRef.current.innerHTML = '';
      }
      qrInstanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    const updateQRCode = async () => {
      if (!qrContainerRef.current || isLoadingData) return;

      const previewBoxSize = 280;

      const qrData = cachedQrData || writeData();

      if (qrContainerRef.current) {
        qrContainerRef.current.style.opacity = '0';
        const instance = await renderQrCodePreview({
          container: qrContainerRef.current,
          data: qrData,
          size: previewBoxSize,
          styling: formData.styling,
          destinationType: formData.destinationType,
          margin: 5,
        });

        qrInstanceRef.current = instance;

        requestAnimationFrame(() => {
          if (!isQrCodeReady) {
            window.setTimeout(() => {
              if (qrContainerRef.current) {
                qrContainerRef.current.style.opacity = '1';
                setIsQrCodeReady(true);
              }
            }, 100);
          } else {
            if (qrContainerRef.current) {
              qrContainerRef.current.style.opacity = '1';
            }
          }
        });
      }
    };

    updateQRCode();
  }, [
    // Visual styling properties that should update immediately
    formData.styling.foregroundColor,
    formData.styling.backgroundColor,
    formData.styling.dotType,
    formData.styling.shape,
    formData.styling.hasLogo,
    formData.styling.logoType,
    formData.styling.customLogoUrl,
    formData.styling.hasBorder,
    // Text and data properties that only update on blur
    formData.styling.topText,
    formData.styling.bottomText,
    formData.destinationType,
    // Cached QR data that updates on blur
    cachedQrData,
    // Refresh trigger for blur-based updates
    qrRefreshTrigger,
    // Loading state to prevent flash
    isLoadingData,
  ]);

  useEffect(() => {
    if (isStaleEditLink) navigate('/qr-codes', { replace: true });
  }, [isStaleEditLink, navigate]);

  useEffect(() => {
    const loadQRCodeData = async () => {
      if (!isEditMode || !editCompanyId) return;

      setIsLoadingData(true);
      setIsQrCodeReady(false);
      try {
        const response = editId
          ? await getQRCodeApi(editId, editCompanyId)
          : await getKeywordQRCodeApi(editKeywordId as string, editCompanyId);
        const qrCode = response.data;

        const parsedData = parseQRCodeData(qrCode);

        setStoredDestination({ type: qrCode.qrcodeTypeName as QRCodeType, data: qrCode.qrCodeData });
        setFormData({
          id: qrCode.id,
          keywordId: qrCode.keywordId,
          name: parsedData.name,
          destinationType: parsedData.destinationType,
          destinationData: parsedData.destinationData,
          previewFormat: parsedData.previewFormat,
          previewSize: parsedData.previewSize,
          styling: parsedData.styling,
        });
        setIsEditedCodeLoaded(true);
      } catch (error) {
        // An archived or unreachable code leaves nothing to edit: the form would stay empty with a
        // read-only destination and an Update button.
        reportErrorToUser(error, 'Could not open the QR code.');
        navigate('/qr-codes');
      } finally {
        setIsLoadingData(false);
      }
    };

    loadQRCodeData();
  }, [isEditMode, editCompanyId, editId, editKeywordId, getQRCodeApi, getKeywordQRCodeApi, navigate]);

  // Update cached QR data after form data is loaded in edit mode
  useEffect(() => {
    if (isEditedCodeLoaded && !isLoadingData) {
      setCachedQrData(writeData());
      setExpandedAccordion('design');
      setInitialFormSnapshot(JSON.stringify(formData));
      if (!colorPalette.includes(formData.styling.foregroundColor.toUpperCase())) {
        setLastCustomColor(formData.styling.foregroundColor);
      }
    }
  }, [isEditedCodeLoaded, isLoadingData]);

  useEffect(() => {
    if (!isEditMode && !initialFormSnapshot) {
      setInitialFormSnapshot(JSON.stringify(formData));
    }
  }, []);

  useEffect(() => {
    const currentColor = formData.styling.foregroundColor;
    if (currentColor && !colorPalette.includes(currentColor.toUpperCase())) {
      setLastCustomColor(currentColor);
    }
  }, [formData.styling.foregroundColor]);

  const handleFormChange = (field: keyof QRCodeFormData, value: unknown) => {
    setFormData(prev => {
      const newData = {
        ...prev,
        [field]: value,
      };

      // Auto-set logo when destination type changes
      if (field === 'destinationType') {
        const logoConfig = getDefaultLogo(value as QRCodeDestinationType);
        newData.styling = {
          ...prev.styling,
          hasLogo: logoConfig.hasLogo,
          logoType: logoConfig.logoType || 'instagram',
        };
      }

      return newData;
    });
  };

  const handleDestinationDataChange = (data: Record<string, unknown>) => {
    setFormData(prev => ({
      ...prev,
      destinationData: data,
    }));
  };

  const handleStylingChange = (field: keyof QRCodeStylingOptions, value: string | boolean | number) => {
    setFormData(prev => ({
      ...prev,
      styling: {
        ...prev.styling,
        [field]: value,
      },
    }));
  };

  // Function to trigger QR code refresh (used on blur events)
  const triggerQRRefresh = () => {
    setCachedQrData(writeData());
    setQrRefreshTrigger(prev => prev + 1);
  };

  const colorPalette = [
    '#000000',
    '#6B6B6B',
    '#8F8F8F',
    '#7B4B3E',
    '#A46453',
    '#BF5106',
    '#FF6C08',
    '#BF0000',
    '#FF0000',
    '#AC2974',
    '#E6379A',
    '#FFD1DC',
    '#FF9AC4',
    '#532390',
    '#6F2FC0',
    '#0C0879',
    '#100BA1',
    '#015BBF',
    '#027AFF',
    '#1AADAD',
    '#18DCDC',
    '#1C9C64',
    '#22BF7B',
  ];

  const getDefaultLogo = (
    destinationType: QRCodeDestinationType,
  ): { hasLogo: boolean; logoType?: 'instagram' | 'facebook' | 'google' } => {
    switch (destinationType) {
      case 'Instagram':
        return { hasLogo: true, logoType: 'instagram' };
      case 'Facebook':
        return { hasLogo: true, logoType: 'facebook' };
      case 'Google Review':
        return { hasLogo: true, logoType: 'google' };
      default:
        return { hasLogo: false };
    }
  };

  const validateAllFormFields = () =>
    validateAllFields(formData, formData.destinationType, formData.name);

  const handleSave = async () => {
    // The code is loaded by the company of the link, so it is saved back to that same company:
    // switching companies in the header while the page is open must not move the record.
    const companyId = editCompanyId ?? currentCompanyId;
    if (!companyId) {
      console.error('Missing company ID');
      return;
    }

    if (!validateAllFormFields()) {
      console.error('Validation failed - missing or invalid required fields');
      return;
    }

    if (!writeData()) {
      console.error('Cannot generate QR data - missing destination information');
      return;
    }

    await save({
      companyId: companyId,
      recordId: formData.id,
      keywordId: formData.keywordId,
      name: formData.name,
      formData: formData,
      destinationType: formData.destinationType,
      storedDestination: storedDestination,
    });
  };

  const handleDownload = async () =>
    handleDownloadImpl(
      formData,
      formData.destinationType,
      formData.name,
      companyName,
      writeData(),
    );

  if (isLoadingData) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={400}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <>
      <Box mb={1.5}>
        <Breadcrumb
          items={[
            { label: 'QR Codes', path: '/qr-codes' },
            {
              label: isEditMode ? 'Edit QR Code' : 'Create QR Code',
              isActive: true,
            },
          ]}
        />
      </Box>
      <ReactRouterPrompt when={!isSaving && initialFormSnapshot !== JSON.stringify(formData)}>
        {({ isActive, onConfirm, onCancel }) =>
          isActive && (
            <BhiDialog open={true} title="You have unsaved changes!">
              <DialogContent>Do you want to continue without saving?</DialogContent>
              <DialogActions>
                <Button onClick={onCancel} color="primary" variant="outlined">
                  No
                </Button>
                <Button onClick={onConfirm} color="secondary" variant="contained">
                  Yes
                </Button>
              </DialogActions>
            </BhiDialog>
          )
        }
      </ReactRouterPrompt>
      <Box display="flex">
        <Box
          width="60%"
          minWidth={0}
          mr={10}
          my={2.5}
          display="flex"
          flexDirection="column"
          gap={2.5}
        >
          <TextInput
            label="QR Code Name"
            value={formData.name}
            onChange={(_, value) => handleFormChange('name', value)}
            onBlur={triggerQRRefresh}
            placeholder="Enter QR code name"
            margin="none"
          />

          <Accordion
            expanded={expandedAccordion === 'destination'}
            onChange={() =>
              setExpandedAccordion(expandedAccordion === 'destination' ? null : 'destination')
            }
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>Scan destination</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2" color="textSecondary" mb={2}>
                Where should your QR code scan to?
              </Typography>
              <ScanDestinationSection
                destinationType={formData.destinationType}
                destinationData={formData.destinationData}
                onDestinationTypeChange={type => handleFormChange('destinationType', type)}
                onDestinationDataChange={handleDestinationDataChange}
                onBlur={triggerQRRefresh}
                isEditMode={isEditMode}
              />
            </AccordionDetails>
          </Accordion>

          <Accordion
            expanded={expandedAccordion === 'design'}
            onChange={() => setExpandedAccordion(expandedAccordion === 'design' ? null : 'design')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>QR Code Design</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <QRCodeAppearanceOptions
                styling={formData.styling}
                colorPalette={colorPalette}
                lastCustomColor={lastCustomColor}
                onLastCustomColorChange={setLastCustomColor}
                onStylingChange={handleStylingChange}
              />
              {/* Logo Section */}
              {!['Instagram', 'Facebook', 'Google Review'].includes(formData.destinationType) && (
                <Box mb={3}>
                  <Typography variant="subtitle2" mb={1} fontWeight={600}>
                    Add your logo to the center of your code
                  </Typography>

                  <input
                    ref={logoFileInputRef}
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = event => {
                          const result = event.target?.result as string;
                          handleStylingChange('customLogoUrl', result);
                          handleStylingChange('hasLogo', true);
                          handleStylingChange('logoType', 'custom');
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                  />

                  {!formData.styling.customLogoUrl ? (
                    <DashedDropZone
                      onDragOver={e => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onDrop={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        const file = e.dataTransfer.files?.[0];
                        if (file && file.type.startsWith('image/')) {
                          const reader = new FileReader();
                          reader.onload = event => {
                            const result = event.target?.result as string;
                            handleStylingChange('customLogoUrl', result);
                            handleStylingChange('hasLogo', true);
                            handleStylingChange('logoType', 'custom');
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                      sx={{
                        p: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <UploadIcon fontSize="medium" color="disabled" />
                        <Typography variant="body2" color="textSecondary">
                          Drag & drop or click to upload a logo
                        </Typography>
                      </Box>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          logoFileInputRef.current?.click();
                        }}
                      >
                        Upload
                      </Button>
                    </DashedDropZone>
                  ) : (
                    <Box>
                      <Box
                        border={1}
                        borderColor="divider"
                        borderRadius={2}
                        sx={{
                          p: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 2,
                        }}
                      >
                        <img
                          src={formData.styling.customLogoUrl}
                          alt="Custom logo preview"
                          style={{ maxWidth: '80px', maxHeight: '80px', objectFit: 'contain' }}
                          onError={e => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => {
                            handleStylingChange('customLogoUrl', '');
                            handleStylingChange('hasLogo', false);
                          }}
                        >
                          Clear
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
              {/* Border Text Section - Only show for square shapes */}
              <Box>
                {/* Border Toggle */}
                <Box display="flex" alignItems="center" mb={2}>
                  <Typography variant="subtitle2" fontWeight={600} mr={2}>
                    Frame
                  </Typography>
                  <Switch
                    checked={formData.styling.hasBorder || false}
                    onChange={e => {
                      const showBorder = e.target.checked;
                      handleStylingChange('hasBorder', showBorder);
                      if (!showBorder) {
                        handleStylingChange('topText', '');
                        handleStylingChange('bottomText', '');
                        setLocalTopText('');
                        setLocalBottomText('');
                      }
                    }}
                  />
                  <Typography variant="body2" color="textSecondary" ml={1}>
                    {formData.styling.hasBorder ? 'On' : 'Off'}
                  </Typography>
                </Box>

                {formData.styling.hasBorder && (
                  <Box display="flex" gap={2} mb={2}>
                    <TextInput
                      label="Top Text"
                      value={localTopText}
                      onChange={(_, value) => setLocalTopText(value)}
                      onBlur={e => {
                        handleStylingChange('topText', e.target.value);
                        triggerQRRefresh();
                      }}
                      placeholder="Text above QR code"
                      margin="none"
                    />
                    <TextInput
                      label="Bottom Text"
                      value={localBottomText}
                      onChange={(_, value) => setLocalBottomText(value)}
                      onBlur={e => {
                        handleStylingChange('bottomText', e.target.value);
                        triggerQRRefresh();
                      }}
                      placeholder="Text below QR code"
                      margin="none"
                    />
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>

          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button variant="outlined" onClick={() => navigate('/qr-codes')}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={isSaving || !validateAllFormFields()}
            >
              {isEditMode ? 'Update' : 'Create'}
            </Button>
          </Box>
        </Box>

        <Panel
          variant="elevation"
          elevation={0}
          sx={{
            position: 'fixed',
            right: '2.5%',
            top: 147,
            width: '27%',
            minWidth: 300,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1.5,
            pt: 0,
          }}
        >
          <Box
            minWidth={320}
            minHeight={360}
            display="flex"
            alignItems="center"
            justifyContent="center"
            mb={1.5}
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              overflow="visible"
              p={2.5}
              minHeight={360}
            >
              <div
                ref={qrContainerRef}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '320px',
                  opacity: isQrCodeReady ? 1 : 0,
                }}
              />
            </Box>
          </Box>

          <Stack width="100%" gap={1.5}>
            <Dropdown
              label="File Format"
              value={formData.previewFormat}
              options={formatOptions}
              onChange={(_, value) => handleFormChange('previewFormat', value)}
              margin="none"
            />
            <Dropdown
              label="Size"
              value={formData.previewSize}
              options={sizeOptions}
              onChange={(_, value) => handleFormChange('previewSize', value)}
              margin="none"
            />
            <Button
              fullWidth
              size="medium"
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleDownload}
              disabled={!validateAllFormFields()}
            >
              Download
            </Button>
          </Stack>
        </Panel>
      </Box>

      {/* Loading Spinner */}
      <Backdrop sx={{ zIndex: t => t.zIndex.drawer + 1 }} open={isLoadingData}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2} color="common.white">
          <CircularProgress color="inherit" size={20} />
          <Typography variant="h6" color="inherit">
            Loading QR Code Data...
          </Typography>
        </Box>
      </Backdrop>
    </>
  );
}
